import * as React from 'react';
import {
  Button,
  Checkbox,
  Dropdown,
  DropdownItem,
  DropdownList,
  Flex,
  MenuToggle,
  MenuToggleElement,
  PageSection,
  Pagination,
  SearchInput,
  Select,
  SelectOption,
  Content,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
} from '@patternfly/react-core';
import { PageContextActions, PageHeaderTitle } from '@app/components/PageContextActions';
import { StatusLabel } from '@app/components/StatusLabel';
import { TruncatedLabel } from '@app/components/TruncatedLabel';
import { getMockDevices, type Device } from '@app/data/deviceData';
import {
  CheckCircleIcon,
  EllipsisVIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  PauseCircleIcon,
  SyncAltIcon,
} from '@patternfly/react-icons';

const Devices: React.FunctionComponent = () => {
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const [selectedFilter, setSelectedFilter] = React.useState('All devices');
  const [searchValue, setSearchValue] = React.useState('');
  const [selectedDevices, setSelectedDevices] = React.useState<Set<string>>(new Set());
  const [openDropdowns, setOpenDropdowns] = React.useState<Record<string, boolean>>({});

  // Pagination state
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(10);

  const getApplicationStatusLabel = (status: Device['applicationStatus']) => {
    const statusConfig = {
      Healthy: { status: 'success' as const },
      Degraded: { status: 'warning' as const },
      Error: { status: 'danger' as const },
      Unknown: { status: 'unknown' as const },
    };
    const config = statusConfig[status];
    return <StatusLabel status={config.status}>{status}</StatusLabel>;
  };

  const getDeviceStatusLabel = (status: Device['deviceStatus']) => {
    const statusConfig = {
      Online: { status: 'success' as const },
      Suspended: { status: 'warning' as const },
      'Pending Sync': { status: 'info' as const, pulse: true },
      Error: { status: 'danger' as const },
    };
    const config = statusConfig[status];
    return (
      <StatusLabel status={config.status} pulse={'pulse' in config && config.pulse}>
        {status}
      </StatusLabel>
    );
  };

  const getSystemUpdateStatusLabel = (status: Device['systemUpdateStatus']) => {
    const statusConfig = {
      'Up-to-date': { status: 'success' as const },
      Updating: { status: 'info' as const, pulse: true },
      'Out-of-date': { status: 'warning' as const },
      Unknown: { status: 'unknown' as const },
    };
    const config = statusConfig[status];
    return (
      <StatusLabel status={config.status} pulse={'pulse' in config && config.pulse}>
        {status}
      </StatusLabel>
    );
  };

  const handleSelectDevice = (deviceId: string, isSelected: boolean) => {
    setSelectedDevices(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(deviceId);
      } else {
        newSet.delete(deviceId);
      }
      return newSet;
    });
  };

  // Filter devices based on search
  const allDevices = React.useMemo(() => getMockDevices(), []);

  const filteredDevices = React.useMemo(() => {
    const query = searchValue.toLowerCase();
    return allDevices.filter((device) => {
      const matchesFilter = selectedFilter === 'All devices' || device.deviceStatus === selectedFilter;
      const matchesSearch =
        !query ||
        device.alias.toLowerCase().includes(query) ||
        device.name.toLowerCase().includes(query) ||
        device.fleet.toLowerCase().includes(query);
      return matchesFilter && matchesSearch;
    });
  }, [allDevices, searchValue, selectedFilter]);

  // Pagination logic
  const startIndex = (page - 1) * perPage;
  const endIndex = startIndex + perPage;
  const paginatedDevices = filteredDevices.slice(startIndex, endIndex);

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPage: number) => {
    setPage(newPage);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  React.useEffect(() => {
    setPage(1);
  }, [searchValue, selectedFilter]);

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setSelectedDevices(new Set(paginatedDevices.map(d => d.id)));
    } else {
      setSelectedDevices(new Set());
    }
  };

  const isAllSelected = selectedDevices.size === paginatedDevices.length && paginatedDevices.length > 0;
  const isPartiallySelected = selectedDevices.size > 0 && selectedDevices.size < paginatedDevices.length;

  const onActionDropdownToggle = (deviceId: string) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [deviceId]: !prev[deviceId]
    }));
  };

  const onActionDropdownSelect = (deviceId: string) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [deviceId]: false
    }));
  };

  const onFilterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const onFilterSelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, value: string | number | undefined) => {
    setSelectedFilter(value as string);
    setIsFilterOpen(false);
  };

  const filterToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={onFilterToggle}
      isExpanded={isFilterOpen}
      style={{ width: '100%', maxWidth: '200px' }}
    >
      {selectedFilter}
    </MenuToggle>
  );

  return (
    <>
      {/* Page Header with Toolbar - Single Line */}
      <PageSection hasBodyWrapper={false} className="acm-page__header-section">
        <div className="acm-page__header-inner">
        <Flex 
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsCenter' }}
            style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}
        >
          <PageHeaderTitle
            title="Devices"
            description="View and manage devices enrolled in your edge environment."
          />
          <PageContextActions placement="toolbar" />
        </Flex>
        </div>
      </PageSection>

      {/* Main Content — white panel: filters, search, CTA, table, pagination */}
      <PageSection hasBodyWrapper={false} className="acm-page__content-section">
        <div className="acm-page__panel">
          <div className="acm-page__panel-toolbar">
            <Toolbar>
              <ToolbarContent>
                {selectedDevices.size > 0 ? (
                  <ToolbarGroup align={{ default: 'alignStart' }}>
                    <ToolbarItem>
                      <Content component="p" style={{ margin: 0, marginRight: 'var(--pf-t--global--spacer--md)' }}>
                        {selectedDevices.size} selected
                      </Content>
                    </ToolbarItem>
                    <ToolbarItem>
                      <Button variant="danger" onClick={() => setSelectedDevices(new Set())}>
                        Delete
                      </Button>
                    </ToolbarItem>
                  </ToolbarGroup>
                ) : (
                  <ToolbarGroup align={{ default: 'alignStart' }}>
                      <ToolbarItem>
                        <Select
                          toggle={filterToggle}
                          onSelect={onFilterSelect}
                          selected={selectedFilter}
                          isOpen={isFilterOpen}
                          onOpenChange={(isOpen) => setIsFilterOpen(isOpen)}
                        >
                          <SelectOption value="All devices">All devices</SelectOption>
                          <SelectOption value="Online">Online</SelectOption>
                          <SelectOption value="Suspended">Suspended</SelectOption>
                          <SelectOption value="Pending Sync">Pending Sync</SelectOption>
                          <SelectOption value="Error">Error</SelectOption>
                          <SelectOption value="Decommissioned">Decommissioned</SelectOption>
                        </Select>
                      </ToolbarItem>
                      <ToolbarItem>
                        <SearchInput
                          className="app-search-input"
                          placeholder="Search"
                          value={searchValue}
                          onChange={(_, value) => setSearchValue(value)}
                          onClear={() => setSearchValue('')}
                        />
                      </ToolbarItem>
                      <ToolbarItem>
                        <Button variant="primary">
                          Add devices
                        </Button>
                      </ToolbarItem>
                    </ToolbarGroup>
                )}
              </ToolbarContent>
            </Toolbar>
          </div>

            {/* Devices Table */}
            <div className="app-data-table">
              {/* Table Header */}
              <div className="app-data-table__header app-data-table__grid app-data-table__grid--devices">
                <div>
                  <Checkbox
                    id="select-all-devices"
                    isChecked={isAllSelected ? true : isPartiallySelected ? null : false}
                    onChange={(_, checked) => handleSelectAll(checked)}
                    aria-label="Select all devices"
                  />
                </div>
                <div>Name</div>
                <div>Alias</div>
                <div>Fleet</div>
                <div>Application status</div>
                <div>Device status</div>
                <div>System status</div>
                <div></div>
              </div>

              {/* Table Rows */}
              {paginatedDevices.map((device, index) => (
                <div
                  key={device.id}
                  className="app-data-table__row app-data-table__grid app-data-table__grid--devices"
                  style={{
                    borderBottom: index < paginatedDevices.length - 1 
                      ? '1px solid var(--pf-t--global--color--nonstatus--gray--200)' 
                      : 'none',
                  }}
                >
                  <div className="app-data-table__control app-data-table__control--select">
                    <Checkbox
                      id={`select-device-${device.id}`}
                      isChecked={selectedDevices.has(device.id)}
                      onChange={(_, checked) => handleSelectDevice(device.id, checked)}
                      aria-label={`Select ${device.alias}`}
                    />
                  </div>
                  <div data-label="Name">
                    <a
                      href="#"
                      onClick={(e) => e.preventDefault()}
                      style={{
                        color: 'var(--pf-t--global--color--brand--default)',
                        textDecoration: 'none',
                      }}
                    >
                      <TruncatedLabel content={device.alias} />
                    </a>
                  </div>
                  <div data-label="Alias">
                    <TruncatedLabel content={device.name} />
                  </div>
                  <div data-label="Fleet">
                    <TruncatedLabel content={device.fleet} />
                  </div>
                  <div data-label="Application status">
                    {getApplicationStatusLabel(device.applicationStatus)}
                  </div>
                  <div data-label="Device status">
                    {getDeviceStatusLabel(device.deviceStatus)}
                  </div>
                  <div data-label="System status">
                    {getSystemUpdateStatusLabel(device.systemUpdateStatus)}
                  </div>
                  <div className="app-data-table__control app-data-table__control--actions">
                    <Dropdown
                      isOpen={openDropdowns[device.id] || false}
                      onSelect={() => onActionDropdownSelect(device.id)}
                      onOpenChange={(isOpen: boolean) => {
                        setOpenDropdowns(prev => ({
                          ...prev,
                          [device.id]: isOpen
                        }));
                      }}
                      toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                        <MenuToggle
                          ref={toggleRef}
                          onClick={() => onActionDropdownToggle(device.id)}
                          isExpanded={openDropdowns[device.id] || false}
                          variant="plain"
                          icon={<EllipsisVIcon />}
                          aria-label={`Actions for ${device.alias}`}
                        />
                      )}
                    >
                      <DropdownList>
                        <DropdownItem
                          key="view-details"
                          onClick={() => onActionDropdownSelect(device.id)}
                        >
                          View
                        </DropdownItem>
                        <DropdownItem
                          key="edit"
                          onClick={() => onActionDropdownSelect(device.id)}
                        >
                          Edit
                        </DropdownItem>
                        <DropdownItem
                          key="delete"
                          onClick={() => onActionDropdownSelect(device.id)}
                        >
                          Delete
                        </DropdownItem>
                      </DropdownList>
                    </Dropdown>
                  </div>
                </div>
              ))}
            </div>

          <div className="acm-page__panel-footer">
              <Pagination
                itemCount={filteredDevices.length}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                widgetId="devices-pagination"
                onPerPageSelect={onPerPageSelect}
                isCompact
              />
          </div>
        </div>
    </PageSection>
    </>
  );
};

export { Devices };

